/**
 * Public landing pages for the invite follow-up cadence:
 *   /invite/snooze/:token  → user clicked "Snooze for 7 days" in an email
 *   /invite/decline/:token → user clicked "Stop these emails"
 *   /invite/refresh        → user clicked "Request a fresh invite" after expiry
 *
 * No auth required — the action_token (or the email entered) is the credential.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle, MoonStars, EnvelopeSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/invite`;

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">S</div>
          <div className="text-sm font-semibold text-zinc-900">SaarOne</div>
        </div>
      </header>
      <main className="flex-1 max-w-xl mx-auto w-full px-4 py-12">{children}</main>
      <footer className="text-center text-xs text-zinc-400 py-6">Powered by SaarOne · saarone.com</footer>
    </div>
  );
}

export function InviteSnooze() {
  return <ActionPage action="snooze" />;
}

export function InviteDecline() {
  return <ActionPage action="decline" />;
}

function ActionPage({ action }) {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/${token}/state`);
        if (cancelled) return;
        setState({ loading: false, data: r.data });
        // If already activated/declined, surface that without auto-firing
        if (r.data.state === "activated" || r.data.state === "declined") return;
        // Auto-confirm by hitting the action endpoint on mount — recipients
        // expect a single-click experience from email.
        const ar = await axios.post(`${API}/${token}/${action}`);
        if (cancelled) return;
        setDone(ar.data);
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: e?.response?.data?.detail || "This link is no longer active." });
      }
    })();
    return () => { cancelled = true; };
  }, [token, action]);

  if (state.loading) {
    return <Shell><Card className="p-8 text-center text-zinc-500">Working on it…</Card></Shell>;
  }
  if (state.error) {
    return <Shell><Card className="p-8 text-center">
      <Warning size={36} weight="bold" className="mx-auto text-rose-600 mb-2" />
      <div className="font-semibold">{state.error}</div>
      <div className="text-xs text-zinc-500 mt-1">You can safely close this tab.</div>
    </Card></Shell>;
  }

  const tenant = state.data?.tenant_name || "your workspace";
  const name = (state.data?.recipient_name || "").split(" ")[0] || "there";

  // Already done previously
  if (state.data?.state === "activated") {
    return <Shell><Card className="p-8 text-center">
      <CheckCircle size={36} weight="fill" className="mx-auto text-emerald-600 mb-2" />
      <div className="font-semibold text-lg text-zinc-900">You're already in!</div>
      <div className="text-sm text-zinc-600 mt-1">You've already activated your {tenant} workspace, {name}. Reminders are off.</div>
    </Card></Shell>;
  }
  if (state.data?.state === "declined") {
    return <Shell><Card className="p-8 text-center">
      <CheckCircle size={36} weight="fill" className="mx-auto text-zinc-500 mb-2" />
      <div className="font-semibold text-lg text-zinc-900">We won't email you again</div>
      <div className="text-sm text-zinc-600 mt-1">Reminders for {tenant} are off. Sorry to see you go, {name}.</div>
    </Card></Shell>;
  }

  if (action === "snooze") {
    const until = done?.until || state.data?.snooze_until;
    const untilStr = until ? new Date(until).toLocaleDateString(undefined, { dateStyle: "medium" }) : "next week";
    return <Shell><Card className="p-8 text-center">
      <MoonStars size={36} weight="duotone" className="mx-auto text-indigo-600 mb-2" />
      <div className="font-semibold text-lg text-zinc-900">Snoozed — see you in 7 days</div>
      <div className="text-sm text-zinc-600 mt-1">
        Got it, {name}. We'll pause emails about your {tenant} workspace until <strong>{untilStr}</strong>.
        Your invite link stays valid the whole time.
      </div>
      <div className="text-xs text-zinc-400 mt-4">You can close this tab.</div>
    </Card></Shell>;
  }
  // declined
  return <Shell><Card className="p-8 text-center">
    <CheckCircle size={36} weight="fill" className="mx-auto text-zinc-500 mb-2" />
    <div className="font-semibold text-lg text-zinc-900">All set — no more emails</div>
    <div className="text-sm text-zinc-600 mt-1">
      We've stopped reminders about {tenant} workspace, {name}. If this was a mistake, just ask your team
      to re-invite you.
    </div>
  </Card></Shell>;
}

export function InviteRefresh() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);

  // We don't have a public endpoint to mint a brand-new token without
  // identifying the user — that would be an open relay. Instead show a
  // friendly "ask your admin" message with a copy-able email body.
  const mailto = `mailto:support@saarone.com?subject=Need%20a%20fresh%20SaarOne%20invite&body=Hi%2C%20my%20SaarOne%20invite%20link%20expired.%20Could%20you%20send%20me%20a%20fresh%20one%3F%20My%20email%20is%20${encodeURIComponent(email)}.`;

  return <Shell><Card className="p-8">
    <EnvelopeSimple size={36} weight="duotone" className="mx-auto text-indigo-600 mb-2" />
    <div className="font-semibold text-lg text-zinc-900 text-center">Request a fresh invite</div>
    <div className="text-sm text-zinc-600 mt-2 text-center">
      Pop us a quick note and we'll send a new link from the team that invited you.
    </div>
    <div className="mt-5">
      <Label className="text-xs">Your email</Label>
      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
    </div>
    <div className="mt-4 flex justify-center">
      {!sent ? (
        <Button onClick={() => { window.location.href = mailto; setSent(true); toast.success("Opening your email app…"); }} className="bg-indigo-600 hover:bg-indigo-700">
          Email support to get a fresh link
        </Button>
      ) : (
        <div className="text-sm text-emerald-700 font-medium">Done — we'll get a fresh link out to {email}.</div>
      )}
    </div>
    <div className="text-[11px] text-zinc-400 mt-6 text-center">
      Or contact the admin who originally invited you — they can resend it directly from their console.
    </div>
  </Card></Shell>;
}
